import React, { useEffect, useState } from "react";
import { Card, CardHeader, Dialog, Bar, Input, ComboBox, ComboBoxItem, AnalyticalTable, Form,
  FormGroup,
  FormItem, CheckBox, 
  Label,
  FileUploader} from '@ui5/webcomponents-react';
import { useSnackbar } from "notistack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import httpRequest from "../../httpRequest";
import currencyData from '../../utils/currency-codes.json';
import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";

function CompanyTable() {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [companyData, setCompanyData] = useState({
    companyId: "",
    companyName: "",
    companyDesc: "",
    baseCurrency: "",
    email: "",
    telephone: "",
    address: "",
    logo: "",
  });
  const [companySmtpSettings, setCompanySmtpSettings] = useState({
    smtpEnabled: false,
    server: "",
    port: "",
    username: "",
    password: "",
    fromName: "",
    useTls: false,
    useSsl: false,
    invoiceWorkflowNotifications: false,
    expenseWorkflowNotifications: false,
    userOnboardingNotifications: false,
    vendorCommunicationNotifications: false,
  });
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null); // For displaying logo preview
  const [file, setFile] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  const currencyOptions = Object.values(currencyData).map(currency => ({
    key: currency.code,
    code: currency.code,
    label: `${currency.code} - ${currency.name}`
  }));
  


  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    setFilteredCompanies(
      companies.filter((company) =>
        company.company_name.toLowerCase().includes(filter.toLowerCase()) ||
        company.company_desc.toLowerCase().includes(filter.toLowerCase()) ||
        company.base_currency.toLowerCase().includes(filter.toLowerCase())
      )
    );
  }, [filter, companies]);
  

  useEffect(() => {
    if (selectedCompanyId) {
      fetchCompanySmtpSettings(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await httpRequest.get(
        `${process.env.REACT_APP_BACKEND_URL}/get-companies?exclude_fields=logo`
      );
      setCompanies(response.data.companies);
    } catch (error) {
      enqueueSnackbar("Failed to fetch companies", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanySmtpSettings = async (companyId) => {
    try {
      const response = await httpRequest.get(
        `${process.env.REACT_APP_BACKEND_URL}/company-smtp-settings?company_id=${companyId}`
      );
      setCompanySmtpSettings({
        smtpEnabled: response.data.smtp_enabled,
        server: response.data.server,
        port: response.data.port,
        username: response.data.username,
        password: "", // password is not returned in the response
        fromName: response.data.from_name,
        useTls: response.data.use_tls,
        useSsl: response.data.use_ssl,
        invoiceWorkflowNotifications: response.data.invoice_workflow_notifications,
        expenseWorkflowNotifications: response.data.expense_workflow_notifications,
        userOnboardingNotifications: response.data.user_onboarding_notifications,
        vendorCommunicationNotifications: response.data.vendor_communication_notifications,
      });
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // If the SMTP settings do not exist for this company, return default settings
        setCompanySmtpSettings({
          smtpEnabled: false,
          server: "",
          port: "",
          username: "",
          password: "",
          fromName: "",
          useTls: false,
          useSsl: false,
          invoiceWorkflowNotifications: false,
          expenseWorkflowNotifications: false,
          userOnboardingNotifications: false,
          vendorCommunicationNotifications: false,
        });
      } else {
        enqueueSnackbar("Failed to fetch company SMTP settings", { variant: "error" });
      }
    }
  };

  const handleFileChange = (event) => {
    const uploadedFile = event.detail.files[0]; // Access the uploaded file

    if (uploadedFile) {
      setFile(uploadedFile);
      
      // Create a preview of the uploaded file if it's an image
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(uploadedFile); // Read the file as a data URL
    } else {
      // Reset if no file is selected
      setFile(null);
      setLogoPreview(null);
    }
  };

  const handleFileSizeExceed = () => {
    alert('File size exceeds the maximum limit (5MB).');
  };
  
  
  const handleAddOrUpdateCompany = async () => {
    try {
        const formData = new FormData();
        formData.append('company_name', companyData.companyName);
        formData.append('company_desc', companyData.companyDesc);
        formData.append('base_currency', companyData.baseCurrency);
        formData.append('email', companyData.email);
        formData.append('telephone', companyData.telephone);
        formData.append('address', companyData.address);
        
   
        if (file) {
          formData.append('logo', file);
          console.log('Logo file:', file);
        }
  
        console.log('FormData:', formData);
  

        // Choose the right endpoint based on whether this is an update or add operation
        const url = selectedCompanyId
            ? `${process.env.REACT_APP_BACKEND_URL}/update-company/${selectedCompanyId}`
            : `${process.env.REACT_APP_BACKEND_URL}/add-company`;

        const companyResponse = await httpRequest({
            method: selectedCompanyId ? 'put' : 'post',
            url,
            data: formData,
        });

        if (companyResponse && companyResponse.data && companyResponse.data.message) {
            enqueueSnackbar(
                selectedCompanyId ? 'Company updated successfully' : 'Company added successfully',
                { variant: 'success' }
            );

            // Handle SMTP settings if enabled
            if (companySmtpSettings.smtpEnabled) {
                const smtpResponse = await httpRequest({
                    method: 'post', // Use POST for creating or updating SMTP settings
                    url: `${process.env.REACT_APP_BACKEND_URL}/company-smtp-settings`,
                    data: {
                        company_id: selectedCompanyId, // Pass company_id in the body
                        smtp_enabled: companySmtpSettings.smtpEnabled,
                        server: companySmtpSettings.server,
                        port: companySmtpSettings.port,
                        username: companySmtpSettings.username,
                        password: companySmtpSettings.password,
                        from_name: companySmtpSettings.fromName,
                        use_tls: companySmtpSettings.useTls,
                        use_ssl: companySmtpSettings.useSsl,
                        invoice_workflow_notifications: companySmtpSettings.invoiceWorkflowNotifications,
                        expense_workflow_notifications: companySmtpSettings.expenseWorkflowNotifications,
                        user_onboarding_notifications: companySmtpSettings.userOnboardingNotifications,
                        vendor_communication_notifications: companySmtpSettings.vendorCommunicationNotifications,
                    },
                });
            }

            fetchCompanies();
            handleCloseAddDialog();
        } else {
            enqueueSnackbar('Failed to save company details', { variant: 'error' });
        }
    } catch (error) {
        enqueueSnackbar(error.response?.data?.error || 'An error occurred', { variant: 'error' });
    }
};


  const handleDeleteCompany = async (companyId) => {
    try {
      await httpRequest.delete(
        `${process.env.REACT_APP_BACKEND_URL}/delete-company/${companyId}`
      );
      enqueueSnackbar("Company deleted successfully", { variant: "success" });
      fetchCompanies();
    } catch (error) {
      enqueueSnackbar("Failed to delete company. Ensure there are no users assigned to it.", { variant: "error" });
    }
  };

  const handleEditCompany = async (company) => {
    setSelectedCompanyId(company.id);
    setCompanyData({
      companyDesc: company.company_desc,
      companyName: company.company_name,
      baseCurrency: company.base_currency,
      telephone: company.telephone,
      email: company.email,
      address: company.address,
      logo: null,
    });
    fetchCompanySmtpSettings(company.id);
  
    // Fetch the logo data separately using the new endpoint
    const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-logo/${company.id}`);
    let logoBase64 = null;
    if (response.data.logo) {
      logoBase64 = `data:image/jpeg;base64,${response.data.logo}`;
    }
    setLogoPreview(logoBase64);
  
    setOpenAddDialog(true);
  };
  

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setCompanyData({
      companyId: "",
      companyName: "",
      companyDesc: "",
      baseCurrency: "",
      email: "",
      telephone: "",
      address: "",
      logo: null,
    });
    setLogoPreview(null); // Reset logo preview on close
  };
  

  const handleAddButtonClick = () => {
    setCompanyData({
      companyDesc: "",
      companyName: "",
      baseCurrency: "",
      email: "",
      telephone: "",
      address: "",
      logo: null,
    });
    setOpenAddDialog(true);
  };



  const handleChange = (event) => {
    const { name, value } = event.target;
  
    // Extract the currency code from the selected option's label
    const currencyCode = value.split(' - ')[0];
  
    setCompanyData({
      ...companyData,
      [name]: currencyCode,
    });
  };
  
   

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const columns = [
    { accessor: 'company_name', Header: 'Company Name', autoResizable: true },
    { accessor: 'company_desc', Header: 'Company Description', autoResizable: true  },
    { accessor: 'base_currency', Header: 'Base Currency', autoResizable: true  },
    {
      Header: 'Actions',
      width: 200,
      Cell: ({ row }) => (
        <>
          <ButtonOutlined
            onClick={() => handleEditCompany(row.original)}
            style={{ marginRight: "10px" }}
          >
            <EditIcon />
          </ButtonOutlined>
          <ButtonOutlined
            onClick={() => handleDeleteCompany(row.original.id)}
          >
            <DeleteIcon />
          </ButtonOutlined>
        </>
      )
    }
  ];

  return (
    <>
      <Card header={<CardHeader subtitleText="Create and manage companies" titleText="Companies" />} sx={{  width: "100%", overflow: "hidden" }}>
        <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <input
            
            placeholder="Search Companies..."
            value={filter}
            onChange={handleFilterChange}
            style={{ flex: "1", padding: "10px", marginRight: "20px" }}
          />
          <ButtonContained
            variant="contained"
            color="primary"
            onClick={handleAddButtonClick}
            startIcon={<AddIcon />}
          >
            <AddIcon /> Add Company
          </ButtonContained>
        </div>
          <AnalyticalTable
            data={filteredCompanies}
            columns={columns}
            sortable
            visibleRows={8}
            scaleWidthMode="Smart"
            loading={loading}
            style={{ paddingLeft: '20px', paddingRight: '20px', paddingBottom: '20px' }}
          />
      </Card>

      <Dialog
        headerText={selectedCompanyId ? `Edit Company` : `Add Company`}
        open={openAddDialog}
        onAfterClose={handleCloseAddDialog} stretch
        footer={
          <Bar
            design="Footer"
            endContent={
              <>
                <ButtonOutlined
                  style={{ marginRight: 8 }}
                  onClick={handleCloseAddDialog}
                  color="primary"
                >
                  Cancel
                </ButtonOutlined>
                <ButtonContained onClick={handleAddOrUpdateCompany} color="primary">
                {selectedCompanyId ? "Update" : "Add"}
                </ButtonContained>
              </>
            }
          />
        }
      >
        <Form layout="ColumnL2 M2 L3">
          <FormGroup headerText="Current Company Info">
          <FormItem labelContent={<Label>Company Name</Label>}>
              <Input
                name="companyName"
                value={companyData.companyName}
                onInput={handleChange}
                placeholder="Enter company name"
              />
            </FormItem>
            <FormItem labelContent={<Label>Company Description</Label>}>
              <Input
                name="companyDesc"
                value={companyData.companyDesc}
                onInput={handleChange}
                placeholder="Enter company description"
              />
            </FormItem>
            <FormItem labelContent={<Label>Base Currency</Label>}>
              <ComboBox
                name="baseCurrency"
                value={companyData.baseCurrency}
                onChange={handleChange}
              >
                {currencyOptions.map((option) => (
                  <ComboBoxItem key={option.key} text={option.label} />
                ))}
              </ComboBox>
            </FormItem>
          </FormGroup>
          <FormGroup headerText="Company Details">
          <FormItem labelContent={<Label>Email</Label>}>
              <Input
                name="email"
                value={companyData.email}
                onInput={handleChange}
                placeholder="Enter email"
              />
            </FormItem>
            <FormItem labelContent={<Label>Telephone</Label>}>
              <Input
                name="telephone"
                value={companyData.telephone}
                onInput={handleChange}
                placeholder="Enter telephone"
              />
            </FormItem>
            <FormItem labelContent={<Label>Address</Label>}>
              <Input
                name="address"
                value={companyData.address}
                onInput={handleChange}
                placeholder="Enter address"
              />
            </FormItem>
          </FormGroup>
          <FormGroup headerText="Company SMTP Settings">
  <FormItem>
    <CheckBox
      checked={companySmtpSettings.smtpEnabled}
      onChange={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          smtpEnabled: e.target.checked,
        })
      }
      text="Enable Custom SMTP"
    />
  </FormItem>
  <FormItem labelContent={<Label>Server</Label>}>
    <Input
      value={companySmtpSettings.server}
      onInput={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          server: e.target.value,
        })
      }
      placeholder="Enter server"
      disabled={!companySmtpSettings.smtpEnabled} // Disable when SMTP is not enabled
    />
  </FormItem>
  <FormItem labelContent={<Label>Port</Label>}>
    <Input
      value={companySmtpSettings.port}
      onInput={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          port: e.target.value,
        })
      }
      placeholder="Enter port"
      disabled={!companySmtpSettings.smtpEnabled} // Disable when SMTP is not enabled
    />
  </FormItem>
  <FormItem labelContent={<Label>Username</Label>}>
    <Input
      value={companySmtpSettings.username}
      onInput={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          username: e.target.value,
        })
      }
      placeholder="Enter username"
      disabled={!companySmtpSettings.smtpEnabled} // Disable when SMTP is not enabled
    />
  </FormItem>
  <FormItem labelContent={<Label>Password</Label>}>
    <Input
      type="password"
      value={companySmtpSettings.password}
      onInput={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          password: e.target.value,
        })
      }
      placeholder="Enter password"
      disabled={!companySmtpSettings.smtpEnabled} // Disable when SMTP is not enabled
    />
  </FormItem>
  <FormItem labelContent={<Label>From Name</Label>}>
    <Input
      value={companySmtpSettings.fromName}
      onInput={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          fromName: e.target.value,
        })
      }
      placeholder="Enter from name"
      disabled={!companySmtpSettings.smtpEnabled} // Disable when SMTP is not enabled
    />
  </FormItem>
  <FormItem>
    <CheckBox
      checked={companySmtpSettings.useTls}
      onChange={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          useTls: e.target.checked,
        })
      }
      text="Use TLS"
      disabled={!companySmtpSettings.smtpEnabled} // Disable when SMTP is not enabled
    />
  </FormItem>
  <FormItem>
    <CheckBox
      checked={companySmtpSettings.useSsl}
      onChange={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          useSsl: e.target.checked,
        })
      }
      text="Use SSL"
      disabled={!companySmtpSettings.smtpEnabled} // Disable when SMTP is not enabled
    />
  </FormItem>
  <FormItem>
    <CheckBox
      checked={companySmtpSettings.invoiceWorkflowNotifications}
      onChange={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          invoiceWorkflowNotifications: e.target.checked,
        })
      }
      text="Invoice Workflow Notifications"
      disabled={!companySmtpSettings.smtpEnabled} // Disable when SMTP is not enabled
    />
  </FormItem>
  <FormItem>
    <CheckBox
      checked={companySmtpSettings.expenseWorkflowNotifications}
      onChange={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          expenseWorkflowNotifications: e.target.checked,
        })
      }
      text="Expense Workflow Notifications"
      disabled={!companySmtpSettings.smtpEnabled} // Disable when SMTP is not enabled
    />
  </FormItem>
  <FormItem>
    <CheckBox
      checked={companySmtpSettings.vendorCommunicationNotifications}
      onChange={(e) =>
        setCompanySmtpSettings({
          ...companySmtpSettings,
          vendorCommunicationNotifications: e.target.checked,
        })
      }
      text="Vendor Communication Notifications"
      disabled={!companySmtpSettings.smtpEnabled} // Disable when SMTP is not enabled
    />
  </FormItem>
</FormGroup>

<FormGroup headerText="Personalization">
<FormItem labelContent={<Label>Company Logo</Label>}>
    
    <FileUploader
      onChange={handleFileChange}
      onFileSizeExceed={handleFileSizeExceed}
      valueState="None"
      accept=".png,.jpg,.jpeg"
      hideInput
      maxFileSize={5}
    >
      <ButtonOutlined>
        Update
      </ButtonOutlined>
    </FileUploader>
   
  </FormItem>
  <FormItem>

  {logoPreview && <img src={logoPreview} alt="Logo Preview" style={{ width: 200, borderRadius: '10%', marginTop: 8 }} />}
  </FormItem>
</FormGroup>


        </Form>
      </Dialog>

     
    </>
  );
}

export default CompanyTable;
